import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { r2Url } from '@/lib/r2';

// ── Family resolution from SKU prefix ────────────────────────────
const SKU_FAMILY: Record<string, string> = {
  Z: 'Zero', '1': 'One', P: 'Pro', M: 'Master', L: 'Legend', E: 'Elite',
};
const getFamilyFromSku = (sku: string): string => {
  if (!sku) return 'Unknown';
  const prefix = sku.startsWith('AOI-') ? sku[4] : sku[0];
  return SKU_FAMILY[prefix] ?? 'Unknown';
};

const FAMILY_LOGO: Record<string, string> = {
  Zero: '/marketing/family-zero.png',
  One: '/marketing/family-one.png',
  Pro: '/marketing/family-pro.png',
  Master: '/marketing/family-master.png',
  Legend: '/marketing/family-legend.png',
  Elite: '/marketing/family-elite.png',
};

// ── Spec categories to show on tag ───────────────────────────────
const SPEC_CATEGORIES: { types: string[]; label: string }[] = [
  { types: ['cpu'], label: 'CPU' },
  { types: ['ram'], label: 'RAM' },
  { types: ['gpu'], label: 'GPU' },
  { types: ['rom', 'rom_1', 'rom_2', 'rom_3', 'storage'], label: 'Storage' },
];

// ── EPS 36-month calculation ─────────────────────────────────────
const calcMonthly = (rrp: number) => Math.ceil((rrp * 1.35) / 36 * 100) / 100;

interface Props {
  build: {
    id: string;
    sku: string;
    description?: string;
    rrp: number | null;
  };
  activeConfigSkus?: Set<string> | null;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'icon';
  className?: string;
}

export function PrintMarketingTag({
  build,
  activeConfigSkus,
  variant = 'outline',
  size = 'sm',
  className,
}: Props) {
  const [printing, setPrinting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Per-category selected SKUs (array to support multi-select for Storage)
  const [selections, setSelections] = useState<Record<string, string[]>>({});

  // ── Fetch BOM (same fallback chain as BuildDetailPage) ─────────
  const { data: bomRows } = useQuery({
    queryKey: ['tag-bom', build.id, build.sku],
    queryFn: async () => {
      const { data: bcRows } = await supabase
        .from('build_components')
        .select('component_sku, component_type, qty_per_build')
        .eq('build_id', build.id);

      if (bcRows && bcRows.length > 0) {
        return bcRows.map(r => ({ sku: r.component_sku, type: r.component_type || '' }));
      }

      if (!build.sku) return [];
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, build_sku')
        .ilike('build_sku', `%${build.sku}%`);

      return (data ?? [])
        .filter(c => {
          const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
          return entries.includes(build.sku);
        })
        .map(c => ({ sku: c.part_number, type: c.category || '' }));
    },
    enabled: !!build.id,
  });

  // ── Fetch display names + descriptions ─────────────────────────
  const componentSkus = useMemo(
    () => (bomRows || []).map(r => r.sku).filter(Boolean),
    [bomRows],
  );

  const { data: nameMap = {} } = useQuery({
    queryKey: ['tag-names', componentSkus],
    queryFn: async () => {
      if (componentSkus.length === 0) return {};
      const [dnRes, descRes] = await Promise.all([
        supabase
          .from('component_display_names')
          .select('part_number, display_name_b2c')
          .in('part_number', componentSkus),
        supabase
          .from('components')
          .select('part_number, description')
          .in('part_number', componentSkus),
      ]);
      const map: Record<string, string> = {};
      (descRes.data ?? []).forEach((r: any) => {
        if (r.description) map[r.part_number] = r.description;
      });
      (dnRes.data ?? []).forEach((r: any) => {
        if (r.display_name_b2c) map[r.part_number] = r.display_name_b2c;
      });
      return map;
    },
    enabled: componentSkus.length > 0,
  });

  // ── Fetch product image (build_media first, fallback to case component_media) ─
  const caseSku = useMemo(
    () => (bomRows || []).find(r => ['case', 'cas'].includes((r.type || '').toLowerCase()))?.sku,
    [bomRows],
  );

  const { data: productImageUrl } = useQuery({
    queryKey: ['tag-image', build.id, caseSku],
    queryFn: async () => {
      // Try build-level media first
      const { data: buildMedia } = await supabase
        .from('build_media')
        .select('file_path')
        .eq('build_id', build.id)
        .order('created_at', { ascending: true })
        .limit(1);
      if (buildMedia && buildMedia.length > 0) {
        return r2Url(`build-media/${buildMedia[0].file_path}`);
      }

      // Fall back to case component media
      if (!caseSku) return null;
      const { data: comp } = await supabase
        .from('components')
        .select('id')
        .eq('part_number', caseSku)
        .single();
      if (!comp) return null;
      const { data: caseMedia } = await supabase
        .from('component_media')
        .select('file_path')
        .eq('component_id', comp.id)
        .order('created_at', { ascending: true })
        .limit(1);
      if (!caseMedia || caseMedia.length === 0) return null;
      return r2Url(`build-media/${caseMedia[0].file_path}`);
    },
    enabled: !!build.id,
  });

  // ── Group BOM by spec category ─────────────────────────────────
  const categoryGroups = useMemo(() => {
    if (!bomRows || bomRows.length === 0) return [];
    return SPEC_CATEGORIES.map(cat => {
      const matching = bomRows.filter(r =>
        cat.types.includes((r.type || '').toLowerCase()),
      );
      return {
        label: cat.label,
        options: matching.map(r => ({
          sku: r.sku,
          name: nameMap[r.sku] || r.sku,
        })),
      };
    });
  }, [bomRows, nameMap]);

  // Check if any category has multiple options
  const hasMultiple = categoryGroups.some(g => g.options.length > 1);

  // ── Resolve final specs using selections (falls back to activeConfigSkus) ─
  const isStorageCategory = (label: string) => label === 'Storage';

  const getDefaultSelections = useCallback((): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    categoryGroups.forEach(cat => {
      if (cat.options.length <= 1) return;
      if (activeConfigSkus) {
        const matching = cat.options.filter(o => activeConfigSkus.has(o.sku));
        if (matching.length > 0) {
          result[cat.label] = matching.map(o => o.sku);
          return;
        }
      }
      // Fallback: first for non-storage, all for storage
      if (isStorageCategory(cat.label)) {
        result[cat.label] = cat.options.map(o => o.sku);
      } else {
        result[cat.label] = [cat.options[0].sku];
      }
    });
    return result;
  }, [categoryGroups, activeConfigSkus]);

  const getResolvedSpecs = useCallback((): { label: string; values: string[] }[] => {
    const sel = Object.keys(selections).length > 0 ? selections : getDefaultSelections();
    return categoryGroups.map(cat => {
      if (cat.options.length === 0) return { label: cat.label, values: ['—'] };
      if (cat.options.length === 1) return { label: cat.label, values: [cat.options[0].name] };
      const selectedSkus = sel[cat.label] || [cat.options[0].sku];
      const selected = cat.options.filter(o => selectedSkus.includes(o.sku));
      if (selected.length === 0) return { label: cat.label, values: [cat.options[0].name] };
      return { label: cat.label, values: selected.map(s => s.name) };
    });
  }, [categoryGroups, selections, getDefaultSelections]);

  // ── Format price digits (no € symbol — it's in the splash graphic) ─
  const formatRrpDigits = (price: number) => {
    const formatted = new Intl.NumberFormat('en-IE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    const first = formatted[0];
    const rest = formatted.slice(1);
    return { first, rest };
  };

  const formatMonthlyDigits = (price: number) => Math.round(price).toString();

  // ── Open dialog or print directly ──────────────────────────────
  const handleClick = () => {
    const defaults = getDefaultSelections();
    setSelections(defaults);
    if (hasMultiple) {
      setDialogOpen(true);
    } else {
      doPrintTag();
    }
  };

  // ── Generate & print ───────────────────────────────────────────
  const doPrintTag = () => {
    if (!build.rrp) return;
    setPrinting(true);
    setDialogOpen(false);

    const family = getFamilyFromSku(build.sku);
    const familyLogoSrc = FAMILY_LOGO[family] || '';
    const rrp = build.rrp;
    const monthly = calcMonthly(rrp);
    const rrpDigits = formatRrpDigits(rrp);
    const monthlyDigits = formatMonthlyDigits(monthly);
    const specs = getResolvedSpecs();

    const specRowsHtml = specs
      .map(s => {
        if (s.values.length <= 1) {
          return `
      <div class="spec-row">
        <span class="spec-label">${s.label}</span>
        <span class="spec-sep">|</span>
        <span class="spec-value">${s.values[0]}</span>
      </div>`;
        }
        // Multi-value (e.g. Storage with 2 devices): label + pipe span both lines
        const lines = s.values.map(v =>
          `<div class="spec-multi-line">${v}</div>`
        ).join('');
        return `
      <div class="spec-row spec-row-multi">
        <span class="spec-label">${s.label}</span>
        <span class="spec-sep">|</span>
        <div class="spec-value-multi">${lines}</div>
      </div>`;
      })
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Marketing Tag — ${build.sku}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@900&family=Open+Sans:wght@400&display=swap" rel="stylesheet">
  <style>
    @page {
      size: 148mm 210mm;
      margin: 0;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      width: 148mm;
      height: 210mm;
      overflow: hidden;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .tag-page {
      width: 148mm;
      height: 210mm;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
    }

    /* ── Top banner ── */
    .top-banner { width: 100%; }
    .top-banner img { width: 100%; height: auto; display: block; }

    /* ── Family logo ── */
    .family-logo { text-align: center; padding: 10px 0 6px; }
    .family-logo img { height: 49px; width: auto; }

    /* ── Product image ── */
    .product-image {
      text-align: center; flex: 1; display: flex;
      align-items: center; justify-content: center;
      padding: 4px 16px; min-height: 0;
    }
    .product-image img { max-height: 100%; max-width: 100%; object-fit: contain; }

    /* ── Spec list ── */
    .spec-list {
      width: 100%; padding: 6px 24px 2px;
      display: flex; flex-direction: column; align-items: center;
    }
    .spec-row {
      display: inline-flex; align-items: baseline; justify-content: center;
      padding: 2px 0; border-bottom: 0.5px solid #ddd;
      gap: 6px;
    }
    .spec-row:last-child { border-bottom: none; }
    .spec-row-multi {
      align-items: center;
    }
    .spec-label {
      font-family: Calibri, 'Segoe UI', sans-serif;
      font-weight: 700; font-size: 12pt; color: #111;
      flex-shrink: 0;
    }
    .spec-sep {
      font-family: Calibri, 'Segoe UI', sans-serif;
      font-size: 12pt; color: #999;
      flex-shrink: 0; align-self: stretch;
      display: flex; align-items: center;
    }
    .spec-value {
      font-family: Calibri, 'Segoe UI', sans-serif;
      font-weight: 400; font-size: 12pt; color: #333;
    }
    .spec-value-multi {
      font-family: Calibri, 'Segoe UI', sans-serif;
      font-weight: 400; font-size: 12pt; color: #333;
      text-align: center;
    }
    .spec-multi-line {
      padding: 1px 0;
    }

    /* ── Price section ── */
    .price-section {
      width: 100%; padding: 0 24px;
      display: flex; justify-content: center;
      margin-top: 40px;
      margin-bottom: 24px;
    }
    .price-splash-wrap { width: 75%; position: relative; }
    .price-splash-wrap img { width: 100%; height: 80%; object-fit: cover; object-position: center; display: block; }

    /* RRP digits — on the red/pink top banner, right after the baked-in € */
    .rrp-digits {
      position: absolute;
      top: 15%;
      left: 32%;
      color: #fff;
      font-family: 'Nunito', sans-serif;
      font-weight: 900;
      font-size: 22.51pt;
      line-height: 1;
      text-shadow: 0 1px 2px rgba(0,0,0,0.15);
    }

    /* Monthly digits — on the purple bottom banner, right after the baked-in € */
    .monthly-digits {
      position: absolute;
      top: 53%;
      left: 44%;
      color: #fff;
      font-family: 'Nunito', sans-serif;
      font-weight: 900;
      font-size: 22.51pt;
      line-height: 1;
      text-shadow: 0 1px 2px rgba(0,0,0,0.15);
    }

    /* ── Footer ── */
    .footer {
      width: 100%; padding: 8px 24px 12px;
      display: flex; justify-content: space-between; align-items: flex-end;
    }
    .footer-sku {
      font-family: 'Open Sans', sans-serif;
      font-weight: 400; font-size: 10pt; color: #333;
    }
    .footer-logo img { height: 22px; width: auto; }
  </style>
</head>
<body>
  <div class="tag-page">
    <div class="top-banner">
      <img src="/marketing/top-banner.png" alt="AOI Gaming PCs" />
    </div>

    ${familyLogoSrc ? `
    <div class="family-logo">
      <img src="${familyLogoSrc}" alt="${family}" />
    </div>` : ''}

    ${productImageUrl ? `
    <div class="product-image">
      <img src="${productImageUrl}" alt="${build.sku}" />
    </div>` : '<div class="product-image"></div>'}

    <div class="spec-list">
      ${specRowsHtml}
    </div>

    <div class="price-section">
      <div class="price-splash-wrap">
        <img src="/marketing/price-splash.png" alt="" />
        <div class="rrp-digits">${rrpDigits.first}${rrpDigits.rest}</div>
        <div class="monthly-digits">${monthlyDigits}</div>
      </div>
    </div>

    <div class="footer">
      <span class="footer-sku">SKU: ${build.sku}</span>
      <div class="footer-logo">
        <img src="/marketing/intercomp-logo.png" alt="Intercomp" />
      </div>
    </div>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=600,height=850');
    if (!printWindow) { setPrinting(false); return; }

    printWindow.document.write(html);
    printWindow.document.close();

    const images = printWindow.document.querySelectorAll('img');
    let loaded = 0;
    const total = images.length;
    let printed = false;

    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setPrinting(false);
      }, 300);
    };

    if (total === 0) {
      setTimeout(triggerPrint, 100);
    } else {
      images.forEach(img => {
        img.onload = img.onerror = () => {
          loaded++;
          if (loaded >= total) setTimeout(triggerPrint, 400);
        };
      });
      setTimeout(triggerPrint, 5000);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={printing || !build.rrp}
        onClick={handleClick}
        title={!build.rrp ? 'Set RRP before printing tag' : undefined}
      >
        <Tag className="h-4 w-4 mr-1.5" />
        {printing ? 'Generating...' : 'Print Tag'}
      </Button>

      {/* Component selection dialog — only shown when categories have multiple options */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Components for Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {categoryGroups.map(cat => {
              if (cat.options.length <= 1) return null;
              const isMulti = isStorageCategory(cat.label);
              const selected = selections[cat.label] || [];
              return (
                <div key={cat.label}>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">
                    {cat.label}{isMulti ? ' (select all that apply)' : ''}
                  </label>
                  <div className="space-y-1">
                    {cat.options.map(opt => {
                      const isChecked = selected.includes(opt.sku);
                      return (
                        <label
                          key={opt.sku}
                          className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            isChecked
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:bg-muted/50'
                          }`}
                        >
                          <input
                            type={isMulti ? 'checkbox' : 'radio'}
                            name={`tag-${cat.label}`}
                            checked={isChecked}
                            onChange={() => {
                              if (isMulti) {
                                setSelections(prev => {
                                  const cur = prev[cat.label] || [];
                                  const next = isChecked
                                    ? cur.filter(s => s !== opt.sku)
                                    : [...cur, opt.sku];
                                  return { ...prev, [cat.label]: next };
                                });
                              } else {
                                setSelections(prev => ({ ...prev, [cat.label]: [opt.sku] }));
                              }
                            }}
                            className="mt-0.5"
                          />
                          <div className="text-sm">
                            <div className="font-medium">{opt.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{opt.sku}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={doPrintTag}>
              <Tag className="h-4 w-4 mr-1.5" />Generate Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
